/**
 * Rapports PDF des diagnostics de site (questionnaire didactique).
 *
 *  - buildAssessmentPdf      : un diagnostic, avec page de garde (export depuis la fiche site) ;
 *  - buildAssessmentsBookPdf : recueil des derniers diagnostics des sites filtrés du tableau de bord.
 *
 * Généré côté navigateur : les textes des questions (4 langues) vivent dans
 * `data/questionnaireContent.js`. Les données viennent de /api/assessments/.../export.
 */
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { CHAPTERS, ANSWER_LABELS, UI, localized } from '../data/questionnaireContent';
import { formatNumber } from './scoreColors';

// Textes propres aux rapports ({ fr, en, pt, mg }, repli français). en/pt/mg à valider par les provinces.
const TEXT = {
  brand: { fr: 'PATRIMOINE', en: 'PATRIMOINE', pt: 'PATRIMOINE', mg: 'PATRIMOINE' },
  reportLabel: { fr: 'Diagnostic', en: 'Check-up', pt: 'Diagnóstico', mg: 'Fanadihadiana' },
  bookLabel: { fr: 'Recueil des diagnostics', en: 'Check-up report book', pt: 'Coletânea de diagnósticos', mg: 'Fanangonana fanadihadiana' },
  bookSubtitle: {
    fr: 'Dernier diagnostic terminé de chaque site sélectionné, par périmètre',
    en: 'Latest completed check-up of each selected site, by scope',
    pt: 'Último diagnóstico concluído de cada site selecionado, por perímetro',
    mg: 'Fanadihadiana vita farany isaky ny toerana voafidy, isaky ny faritra'
  },
  scope: { fr: 'Périmètre', en: 'Scope', pt: 'Perímetro', mg: 'Faritra' },
  assessmentDate: { fr: 'Date du diagnostic', en: 'Check-up date', pt: 'Data do diagnóstico', mg: 'Daty nanaovana azy' },
  doneBy: { fr: 'Réalisé par', en: 'Carried out by', pt: 'Realizado por', mg: "Nataon'i" },
  version: { fr: 'Version du questionnaire', en: 'Questionnaire version', pt: 'Versão do questionário', mg: "Dikan'ny fanontaniana" },
  investmentsCreated: { fr: 'Investissements créés', en: 'Investments created', pt: 'Investimentos criados', mg: 'Fampiasam-bola noforonina' },
  followUpTitle: {
    fr: 'Investissements créés depuis ce diagnostic',
    en: 'Investments created from this check-up',
    pt: 'Investimentos criados a partir deste diagnóstico',
    mg: "Fampiasam-bola noforonina avy amin'ity fanadihadiana ity"
  },
  noInvestments: {
    fr: "Aucun investissement n'a encore été créé depuis ce diagnostic.",
    en: 'No investment has been created from this check-up yet.',
    pt: 'Ainda não foi criado nenhum investimento a partir deste diagnóstico.',
    mg: "Mbola tsy nisy fampiasam-bola noforonina avy amin'ity fanadihadiana ity."
  },
  investment: { fr: 'Investissement', en: 'Investment', pt: 'Investimento', mg: 'Fampiasam-bola' },
  status: { fr: 'Statut', en: 'Status', pt: 'Estado', mg: 'Toe-javatra' },
  amount: { fr: 'Montant', en: 'Amount', pt: 'Montante', mg: 'Vola' },
  answersTitle: { fr: 'Réponses au questionnaire', en: 'Questionnaire answers', pt: 'Respostas ao questionário', mg: "Valin'ny fanontaniana" },
  noAnswer: { fr: 'Sans réponse', en: 'No answer', pt: 'Sem resposta', mg: 'Tsy misy valiny' },
  contents: { fr: 'Contenu du rapport', en: 'Report contents', pt: 'Conteúdo do relatório', mg: "Votoatin'ny tatitra" },
  bookContentsSummary: {
    fr: 'Synthèse par site', en: 'Summary by site', pt: 'Síntese por site', mg: 'Famintinana isaky ny toerana'
  },
  bookContentsChapters: {
    fr: 'Un chapitre par diagnostic : propositions, investissements créés et réponses',
    en: 'One chapter per check-up: proposals, investments created and answers',
    pt: 'Um capítulo por diagnóstico: propostas, investimentos criados e respostas',
    mg: 'Toko iray isaky ny fanadihadiana: tolo-kevitra, fampiasam-bola noforonina ary valiny'
  },
  selectedSites: { fr: 'Sites sélectionnés', en: 'Selected sites', pt: 'Sites selecionados', mg: 'Toerana voafidy' },
  sitesWithAssessment: { fr: 'Sites avec diagnostic', en: 'Sites with a check-up', pt: 'Sites com diagnóstico', mg: 'Toerana misy fanadihadiana' },
  assessmentsCount: { fr: 'Diagnostics', en: 'Check-ups', pt: 'Diagnósticos', mg: 'Fanadihadiana' },
  filter: { fr: 'Filtre', en: 'Filter', pt: 'Filtro', mg: 'Sivana' },
  noFilter: { fr: 'Aucun (tous les sites)', en: 'None (all sites)', pt: 'Nenhum (todos os sites)', mg: 'Tsy misy (toerana rehetra)' },
  site: { fr: 'Site', en: 'Site', pt: 'Site', mg: 'Toerana' },
  province: { fr: 'Province', en: 'Province', pt: 'Província', mg: 'Faritany' },
  date: { fr: 'Date', en: 'Date', pt: 'Data', mg: 'Daty' },
  // En-têtes courts du tableau de synthèse (colonnes étroites)
  colUrgent: { fr: 'Urgent', en: 'Urgent', pt: 'Urgente', mg: 'Maika' },
  colImportant: { fr: 'Important', en: 'Important', pt: 'Importante', mg: 'Manan-danja' },
  colWatch: { fr: 'À surveiller', en: 'To monitor', pt: 'A vigiar', mg: 'Harahi-maso' },
  colInvestments: { fr: 'Invest. créés', en: 'Invest. created', pt: 'Invest. criados', mg: 'Fampiasam-bola' },
  sitesWithout: {
    fr: 'Sites sans diagnostic terminé', en: 'Sites without a completed check-up',
    pt: 'Sites sem diagnóstico concluído', mg: 'Toerana tsy misy fanadihadiana vita'
  },
  generatedOn: { fr: 'Généré le', en: 'Generated on', pt: 'Gerado em', mg: "Novokarina tamin'ny" },
  page: { fr: 'Page', en: 'Page', pt: 'Página', mg: 'Pejy' }
};

// ─── Mise en page (mm, A4 portrait) ──────────────────────────────────────────
const PAGE_W = 210;
const PAGE_H = 297;
const M = 16;
const CONTENT_W = PAGE_W - 2 * M;
const BODY_TOP = 26;
const BODY_BOTTOM = PAGE_H - 18;

const C = {
  band: [52, 66, 92],
  bandDark: [41, 52, 74],
  bandText: [188, 204, 226],
  bandLight: [221, 230, 241],
  accent: [120, 144, 175],
  ink: [45, 55, 72],
  text: [74, 85, 104],
  muted: [140, 150, 165],
  bg: [245, 247, 250],
  border: [221, 227, 235],
  white: [255, 255, 255],
  green: [39, 174, 96],
  greenFill: [222, 243, 230],
  amberFill: [253, 241, 214]
};

// Mêmes niveaux et couleurs que l'écran de bilan (AssessmentResults)
const BUCKETS = [
  { key: 'urgent', count: 'nb_urgent', ui: 'bucketUrgent', col: 'colUrgent', color: [192, 57, 43], fill: [250, 222, 219] },
  { key: 'important', count: 'nb_important', ui: 'bucketImportant', col: 'colImportant', color: [211, 84, 0], fill: [251, 231, 214] },
  { key: 'surveiller', count: 'nb_surveiller', ui: 'bucketWatch', col: 'colWatch', color: [183, 149, 11], fill: [252, 243, 207] }
];

const STATUS_COLOR = { valide: C.green, rejete: [192, 57, 43], en_attente: [211, 84, 0] };

// Les polices standard de jsPDF (CP1252) ne savent pas rendre les espaces fines insécables
const clean = (v) => String(v ?? '').replace(/[  ]/g, ' ');

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' }) : '—');

// Hauteur d'une ligne de texte en mm pour une taille en points (interligne jsPDF par défaut 1,15)
const lineH = (size) => size * 0.3528 * 1.15;

const slug = (s) => clean(s).normalize('NFD').replace(/[̀-ͯ]/g, '')
  .replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '').toLowerCase();

function fit(doc, text, maxW) {
  let s = clean(text);
  if (doc.getTextWidth(s) <= maxW) return s;
  while (s.length > 1 && doc.getTextWidth(`${s}…`) > maxW) s = s.slice(0, -1);
  return `${s.trimEnd()}…`;
}

// ─── Contexte de rendu ───────────────────────────────────────────────────────

function createContext({ t, lang }) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  return {
    doc, t, lang,
    tx: (key) => localized(TEXT[key], lang),
    ui: (key) => localized(UI[key], lang),
    y: BODY_TOP,
    runningTitle: '',
    // numéro de page → titre courant ; les pages absentes (pages de garde) n'ont ni en-tête ni pied
    pageTitles: {}
  };
}

function markPage(ctx) {
  ctx.pageTitles[ctx.doc.getCurrentPageInfo().pageNumber] = ctx.runningTitle;
}

function newPage(ctx) {
  ctx.doc.addPage();
  markPage(ctx);
  ctx.y = BODY_TOP;
}

function ensureSpace(ctx, h) {
  if (ctx.y + h > BODY_BOTTOM) newPage(ctx);
}

function tableDefaults(ctx) {
  return {
    margin: { left: M, right: M, top: BODY_TOP, bottom: PAGE_H - BODY_BOTTOM },
    styles: {
      font: 'helvetica', fontSize: 8.5, cellPadding: 2.2, textColor: C.text,
      lineColor: C.border, lineWidth: 0.2, valign: 'middle'
    },
    headStyles: { fillColor: C.band, textColor: C.white, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: C.bg },
    // autoTable ajoute ses propres pages : on les enregistre pour l'en-tête et le pied
    didDrawPage: () => markPage(ctx)
  };
}

// En-têtes et pieds dessinés en dernier, quand le nombre total de pages est connu
function finalize(ctx, reportLabel) {
  const { doc } = ctx;
  const total = doc.getNumberOfPages();
  const generated = `${ctx.tx('generatedOn')} ${fmtDate(new Date())}`;
  for (let p = 1; p <= total; p++) {
    if (!(p in ctx.pageTitles)) continue;
    doc.setPage(p);

    doc.setFillColor(...C.bg);
    doc.rect(0, 0, PAGE_W, 16, 'F');
    doc.setFillColor(...C.accent);
    doc.rect(0, 0, PAGE_W, 1.2, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...C.accent);
    doc.text(`${ctx.tx('brand')}  ·  ${clean(reportLabel).toUpperCase()}`, M, 10, { charSpace: 0.4 });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...C.text);
    doc.text(fit(doc, ctx.pageTitles[p], 95), PAGE_W - M, 10, { align: 'right' });

    doc.setDrawColor(...C.border);
    doc.setLineWidth(0.3);
    doc.line(M, PAGE_H - 12, PAGE_W - M, PAGE_H - 12);
    doc.setFontSize(7.5);
    doc.setTextColor(...C.muted);
    doc.text(generated, M, PAGE_H - 7);
    doc.text(`${ctx.tx('page')} ${p} / ${total}`, PAGE_W - M, PAGE_H - 7, { align: 'right' });
  }
}

// ─── Libellés métier ─────────────────────────────────────────────────────────

function scopeLabel(ctx, a) {
  if (!a.batiment_id) return ctx.ui('scopeAllSite');
  const type = a.type_batiment ? ctx.t(`buildingTypes.${a.type_batiment}`, { defaultValue: a.type_batiment }) : '';
  return `${ctx.ui('building')} ${a.numero_batiment ?? ''}${type ? ` — ${type}` : ''}`;
}

function locationLabel(ctx, site) {
  const province = site.province ? ctx.t(`provinces.${site.province}`, { defaultValue: site.province }) : null;
  const place = [site.ville, site.pays].filter(Boolean).join(', ');
  return [province, place].filter(Boolean).join('  ·  ');
}

const personLabel = (a) => `${a.started_by_first_name || ''} ${a.started_by_last_name || ''}`.trim() || '—';

function investmentLabel(ctx, inv) {
  const thematique = inv.thematique === 'autre' && inv.thematique_autre
    ? inv.thematique_autre
    : ctx.t(`thematiques.${inv.thematique}`);
  const items = (inv.item || [])
    .map((it) => (it === 'autre' && inv.item_autre ? inv.item_autre : ctx.t(`riskItems.${it}`)))
    .join(', ');
  return items ? `${thematique} — ${items}` : thematique;
}

function existingLabel(ctx, existing) {
  if (existing.validation_status === 'rejete' && existing.validated_at) {
    return ctx.ui('alreadyExistsRejected').replace('{date}', fmtDate(existing.validated_at));
  }
  return ctx.ui('alreadyExists');
}

function bucketKpis(ctx, counts, investmentsCount) {
  return [
    ...BUCKETS.map((b) => ({ label: ctx.ui(b.ui), value: counts[b.count] || 0, color: b.color, fill: b.fill })),
    { label: ctx.tx('investmentsCreated'), value: investmentsCount, color: C.band, fill: C.bandLight }
  ];
}

// ─── Blocs graphiques ────────────────────────────────────────────────────────

function drawCoverBand(ctx, { eyebrow, title, subtitle, subtitleBold }) {
  const { doc } = ctx;
  doc.setFillColor(...C.band);
  doc.rect(0, 0, PAGE_W, 128, 'F');
  doc.setFillColor(...C.bandDark);
  doc.rect(0, 118, PAGE_W, 10, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(...C.bandText);
  doc.text(clean(eyebrow).toUpperCase(), M, 26, { charSpace: 1.2 });

  doc.setFontSize(30);
  doc.setTextColor(...C.white);
  const titleLines = doc.splitTextToSize(clean(title), CONTENT_W);
  doc.text(titleLines, M, 44);
  let y = 44 + (titleLines.length - 1) * lineH(30) + 7;

  doc.setFillColor(...C.accent);
  doc.rect(M, y, 26, 1.2, 'F');
  doc.setFillColor(...C.bandText);
  doc.rect(M + 28, y, 9, 1.2, 'F');
  y += 12;

  if (subtitleBold) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor(...C.white);
    const lines = doc.splitTextToSize(clean(subtitleBold), CONTENT_W).slice(0, 2);
    doc.text(lines, M, y);
    y += lines.length * lineH(18) + 1;
  }
  if (subtitle) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10.5);
    doc.setTextColor(...C.bandText);
    doc.text(doc.splitTextToSize(clean(subtitle), CONTENT_W).slice(0, 2), M, y);
  }

  doc.setFillColor(...C.bg);
  doc.rect(0, 128, PAGE_W, PAGE_H - 128, 'F');
}

// Carte blanche à cheval sur le bandeau : grille label / valeur sur 2 colonnes
function drawInfoCard(ctx, y, fields) {
  const { doc } = ctx;
  const colW = (CONTENT_W - 16) / 2;
  const rows = Math.ceil(fields.length / 2);
  const rowH = 15;
  const h = 10 + rows * rowH;

  doc.setFillColor(...C.white);
  doc.setDrawColor(...C.border);
  doc.setLineWidth(0.3);
  doc.roundedRect(M, y, CONTENT_W, h, 2.5, 2.5, 'FD');
  doc.setFillColor(...C.accent);
  doc.rect(M, y + 2.5, 1.6, h - 5, 'F');

  fields.forEach((f, i) => {
    const x = M + 9 + (i % 2) * colW;
    const fy = y + 10 + Math.floor(i / 2) * rowH;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(...C.muted);
    doc.text(clean(f.label).toUpperCase(), x, fy, { charSpace: 0.5 });
    doc.setFontSize(11);
    doc.setTextColor(...C.ink);
    doc.text(fit(doc, f.value, colW - 6), x, fy + 6);
  });
  return y + h;
}

function drawKpis(ctx, y, kpis, h = 26) {
  const { doc } = ctx;
  const gap = 4;
  const w = (CONTENT_W - gap * (kpis.length - 1)) / kpis.length;
  kpis.forEach((k, i) => {
    const x = M + i * (w + gap);
    doc.setFillColor(...k.fill);
    doc.roundedRect(x, y, w, h, 2, 2, 'F');
    doc.setFillColor(...k.color);
    doc.rect(x, y + h - 1.4, w, 1.4, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(h > 20 ? 22 : 15);
    doc.setTextColor(...k.color);
    doc.text(String(k.value), x + w / 2, y + (h > 20 ? 12.5 : 8.5), { align: 'center' });
    doc.setFontSize(h > 20 ? 7.5 : 6.8);
    doc.setTextColor(...C.text);
    doc.text(doc.splitTextToSize(clean(k.label), w - 4).slice(0, 2), x + w / 2, y + (h > 20 ? 18.5 : 13.2), { align: 'center' });
  });
  return y + h;
}

function drawContents(ctx, y, items) {
  const { doc } = ctx;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...C.ink);
  doc.text(ctx.tx('contents'), M, y);
  y += 6;
  items.forEach((item, i) => {
    doc.setFillColor(...C.accent);
    doc.circle(M + 2.5, y - 1.2, 2.5, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...C.white);
    doc.text(String(i + 1), M + 2.5, y - 0.1, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.setTextColor(...C.text);
    doc.text(clean(item), M + 8, y);
    y += 7.5;
  });
}

function drawCoverFooter(ctx) {
  const { doc } = ctx;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...C.muted);
  doc.text(`${ctx.tx('generatedOn')} ${fmtDate(new Date())}`, PAGE_W / 2, PAGE_H - 14, { align: 'center' });
}

function drawSectionTitle(ctx, title) {
  const { doc } = ctx;
  ensureSpace(ctx, 40); // jamais de titre seul en bas de page
  doc.setFillColor(...C.accent);
  doc.rect(M, ctx.y, 1.6, 7, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(...C.ink);
  doc.text(clean(title), M + 4.5, ctx.y + 5.4);
  doc.setDrawColor(...C.border);
  doc.setLineWidth(0.3);
  doc.line(M, ctx.y + 10, PAGE_W - M, ctx.y + 10);
  ctx.y += 17;
}

function drawNote(ctx, title, text, color, fill) {
  const { doc } = ctx;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  const lines = doc.splitTextToSize(clean(text), CONTENT_W - 12);
  const h = 11 + lines.length * lineH(9);
  ensureSpace(ctx, h);
  doc.setFillColor(...fill);
  doc.roundedRect(M, ctx.y, CONTENT_W, h, 2, 2, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...color);
  doc.text(clean(title), M + 6, ctx.y + 6.5);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...C.text);
  doc.text(lines, M + 6, ctx.y + 11.5);
  ctx.y += h + 6;
}

// ─── Sections d'un diagnostic ────────────────────────────────────────────────

function drawProposalCard(ctx, p, bucket) {
  const { doc, t } = ctx;
  const innerW = CONTENT_W - 12;
  const items = (p.item || []).map((it) => t(`riskItems.${it}`)).join('  ·  ');
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  const itemLines = items ? doc.splitTextToSize(clean(items), innerW) : [];
  doc.setFontSize(9);
  const descLines = doc.splitTextToSize(clean(p.description), innerW);
  const existing = p.existing ? existingLabel(ctx, p.existing) : null;

  const h = 6.5 + lineH(10.5) + itemLines.length * lineH(8.5) + 1.5
    + descLines.length * lineH(9) + (existing ? lineH(8) + 1.5 : 0) + 2.5;
  ensureSpace(ctx, h + 3);

  const top = ctx.y;
  doc.setFillColor(...C.white);
  doc.setDrawColor(...C.border);
  doc.setLineWidth(0.25);
  doc.roundedRect(M, top, CONTENT_W, h, 1.5, 1.5, 'FD');
  doc.setFillColor(...bucket.color);
  doc.rect(M, top, 1.8, h, 'F');

  let y = top + 6.5;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.setTextColor(...C.ink);
  doc.text(clean(t(`thematiques.${p.thematique}`)), M + 6, y);
  y += lineH(10.5);
  if (itemLines.length) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...C.accent);
    doc.text(itemLines, M + 6, y);
    y += itemLines.length * lineH(8.5);
  }
  y += 1.5;
  doc.setFontSize(9);
  doc.setTextColor(...C.text);
  doc.text(descLines, M + 6, y);
  y += descLines.length * lineH(9);
  if (existing) {
    y += 1.5;
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8);
    doc.setTextColor(...C.muted);
    doc.text(clean(existing), M + 6, y);
  }
  ctx.y = top + h + 3;
}

function drawProposals(ctx, proposals) {
  const { doc } = ctx;
  drawSectionTitle(ctx, ctx.ui('proposalsSection'));
  if (proposals.length === 0) {
    drawNote(ctx, ctx.ui('allGoodTitle'), ctx.ui('allGoodText'), C.green, C.greenFill);
    return;
  }
  for (const bucket of BUCKETS) {
    const items = proposals.filter((p) => p.bucket === bucket.key);
    if (items.length === 0) continue;
    ensureSpace(ctx, 30);
    doc.setFillColor(...bucket.color);
    doc.circle(M + 1.6, ctx.y - 1.3, 1.6, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10.5);
    doc.setTextColor(...bucket.color);
    doc.text(`${clean(ctx.ui(bucket.ui))}  (${items.length})`, M + 5, ctx.y);
    ctx.y += 4.5;
    items.forEach((p) => drawProposalCard(ctx, p, bucket));
    ctx.y += 3;
  }
}

function drawInvestments(ctx, investments, devise) {
  const { doc, t } = ctx;
  drawSectionTitle(ctx, ctx.tx('followUpTitle'));
  if (investments.length === 0) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    doc.setTextColor(...C.muted);
    doc.text(ctx.tx('noInvestments'), M, ctx.y + 2);
    ctx.y += 12;
    return;
  }
  autoTable(doc, {
    ...tableDefaults(ctx),
    startY: ctx.y,
    head: [[ctx.tx('investment'), ctx.tx('status'), { content: ctx.tx('amount'), styles: { halign: 'right' } }]],
    body: investments.map((inv) => [
      clean(investmentLabel(ctx, inv)),
      clean(t(`validationStatus.${inv.validation_status}`)),
      Number(inv.montant_investissement) > 0 ? clean(`${formatNumber(inv.montant_investissement)} ${devise || 'EUR'}`) : '—'
    ]),
    columnStyles: { 1: { cellWidth: 30 }, 2: { cellWidth: 34, halign: 'right' } },
    didParseCell: (data) => {
      if (data.section === 'body' && data.column.index === 1) {
        data.cell.styles.textColor = STATUS_COLOR[investments[data.row.index].validation_status] || C.text;
        data.cell.styles.fontStyle = 'bold';
      }
    }
  });
  ctx.y = doc.lastAutoTable.finalY + 10;
}

function drawAnswers(ctx, answers) {
  const { doc, t, lang } = ctx;
  drawSectionTitle(ctx, ctx.tx('answersTitle'));
  for (const chapter of CHAPTERS) {
    const rows = chapter.questions.map((q) => ({ question: localized(q.question, lang), answer: answers[q.key] }));
    ensureSpace(ctx, 28);
    autoTable(doc, {
      ...tableDefaults(ctx),
      startY: ctx.y,
      head: [[{ content: clean(t(`thematiques.${chapter.thematique}`)), colSpan: 2 }]],
      headStyles: { fillColor: C.bandLight, textColor: C.ink, fontStyle: 'bold', fontSize: 9.5 },
      body: rows.map((r) => [
        clean(r.question),
        r.answer ? clean(localized(ANSWER_LABELS[r.answer], lang)) : ctx.tx('noAnswer')
      ]),
      columnStyles: { 1: { cellWidth: 30, halign: 'center', fontStyle: 'bold', textColor: C.ink } },
      didParseCell: (data) => {
        if (data.section !== 'body' || data.column.index !== 1) return;
        const answer = rows[data.row.index].answer;
        if (!answer) {
          data.cell.styles.textColor = C.muted;
          data.cell.styles.fontStyle = 'italic';
        } else if (answer === 'je_ne_sais_pas') {
          data.cell.styles.fillColor = C.amberFill;
          data.cell.styles.textColor = [176, 96, 0];
        }
      }
    });
    ctx.y = doc.lastAutoTable.finalY + 5;
  }
}

function drawAssessmentBody(ctx, entry) {
  drawProposals(ctx, entry.assessment.proposals || []);
  drawInvestments(ctx, entry.investments, entry.site.devise_pays);
  drawAnswers(ctx, entry.assessment.answers || {});
}

// Bandeau d'ouverture d'un diagnostic dans le recueil
function drawAssessmentBanner(ctx, entry) {
  const { doc } = ctx;
  const { site, assessment: a } = entry;
  const h = 36;
  doc.setFillColor(...C.band);
  doc.roundedRect(M, ctx.y, CONTENT_W, h, 2.5, 2.5, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.setTextColor(...C.white);
  doc.text(fit(doc, site.nom_site, CONTENT_W - 14), M + 7, ctx.y + 10);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(...C.bandText);
  doc.text(fit(doc, locationLabel(ctx, site), CONTENT_W - 14), M + 7, ctx.y + 16);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9.5);
  doc.setTextColor(...C.white);
  doc.text(fit(doc, scopeLabel(ctx, a), CONTENT_W - 14), M + 7, ctx.y + 24.5);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(...C.bandText);
  const meta = `${ctx.tx('assessmentDate')} : ${fmtDate(a.completed_at)}   ·   ${ctx.tx('doneBy')} : ${personLabel(a)}`;
  doc.text(fit(doc, meta, CONTENT_W - 14), M + 7, ctx.y + 30.5);
  ctx.y += h + 5;
  ctx.y = drawKpis(ctx, ctx.y, bucketKpis(ctx, a, entry.investments.length), 18) + 10;
}

// ─── Rapports ────────────────────────────────────────────────────────────────

/** PDF d'un diagnostic (fiche site). `entry` = réponse de GET /assessments/:id/export */
export function buildAssessmentPdf(entry, { t, lang }) {
  const ctx = createContext({ t, lang });
  const { site, assessment: a } = entry;

  drawCoverBand(ctx, {
    eyebrow: `${ctx.tx('brand')}  ·  ${ctx.tx('reportLabel')}`,
    title: ctx.ui('panelTitle'),
    subtitleBold: site.nom_site,
    subtitle: locationLabel(ctx, site)
  });
  let y = drawInfoCard(ctx, 108, [
    { label: ctx.tx('scope'), value: scopeLabel(ctx, a) },
    { label: ctx.tx('assessmentDate'), value: fmtDate(a.completed_at) },
    { label: ctx.tx('doneBy'), value: personLabel(a) },
    { label: ctx.tx('version'), value: a.questionnaire_version }
  ]);
  y = drawKpis(ctx, y + 10, bucketKpis(ctx, a, entry.investments.length));
  drawContents(ctx, y + 16, [ctx.ui('proposalsSection'), ctx.tx('followUpTitle'), ctx.tx('answersTitle')]);
  drawCoverFooter(ctx);

  ctx.runningTitle = `${site.nom_site} — ${scopeLabel(ctx, a)}`;
  newPage(ctx);
  drawAssessmentBody(ctx, entry);
  finalize(ctx, ctx.tx('reportLabel'));

  const filename = `diagnostic-${slug(site.nom_site)}-${a.batiment_id ? `bat-${a.numero_batiment}` : 'site'}-${slug(fmtDate(a.completed_at))}.pdf`;
  return { doc: ctx.doc, filename };
}

/**
 * Recueil des derniers diagnostics des sites filtrés du tableau de bord.
 * @param entries     réponse de POST /assessments/export/latest
 * @param sites       sites affichés (filtrés et triés) : fixe l'ordre des chapitres
 * @param searchQuery filtre de recherche appliqué, rappelé en page de garde
 */
export function buildAssessmentsBookPdf(entries, { t, lang, sites, searchQuery }) {
  const ctx = createContext({ t, lang });
  const order = new Map(sites.map((s, i) => [s.id, i]));
  const sorted = [...entries].sort((a, b) => order.get(a.site.id) - order.get(b.site.id));
  const withAssessment = new Set(sorted.map((e) => e.site.id));
  const sitesWithout = sites.filter((s) => !withAssessment.has(s.id));

  const totals = sorted.reduce((acc, e) => {
    BUCKETS.forEach((b) => { acc[b.count] = (acc[b.count] || 0) + (e.assessment[b.count] || 0); });
    acc.investments += e.investments.length;
    return acc;
  }, { investments: 0 });

  drawCoverBand(ctx, {
    eyebrow: `${ctx.tx('brand')}  ·  ${ctx.tx('reportLabel')}`,
    title: ctx.tx('bookLabel'),
    subtitle: ctx.tx('bookSubtitle')
  });
  let y = drawInfoCard(ctx, 108, [
    { label: ctx.tx('selectedSites'), value: String(sites.length) },
    { label: ctx.tx('sitesWithAssessment'), value: String(withAssessment.size) },
    { label: ctx.tx('assessmentsCount'), value: String(sorted.length) },
    { label: ctx.tx('filter'), value: searchQuery ? `« ${searchQuery} »` : ctx.tx('noFilter') }
  ]);
  y = drawKpis(ctx, y + 10, bucketKpis(ctx, totals, totals.investments));
  drawContents(ctx, y + 16, [ctx.tx('bookContentsSummary'), ctx.tx('bookContentsChapters')]);
  drawCoverFooter(ctx);

  // Synthèse
  ctx.runningTitle = ctx.tx('bookContentsSummary');
  newPage(ctx);
  drawSectionTitle(ctx, ctx.tx('bookContentsSummary'));
  autoTable(ctx.doc, {
    ...tableDefaults(ctx),
    startY: ctx.y,
    styles: { ...tableDefaults(ctx).styles, fontSize: 8 },
    head: [[
      ctx.tx('site'), ctx.tx('scope'), ctx.tx('date'),
      ...BUCKETS.map((b) => ({ content: ctx.tx(b.col), styles: { halign: 'center' } })),
      { content: ctx.tx('colInvestments'), styles: { halign: 'center' } }
    ]],
    body: sorted.map((e) => [
      clean([e.site.nom_site, locationLabel(ctx, e.site)].filter(Boolean).join('\n')),
      clean(scopeLabel(ctx, e.assessment)),
      fmtDate(e.assessment.completed_at),
      ...BUCKETS.map((b) => e.assessment[b.count] || 0),
      e.investments.length
    ]),
    columnStyles: {
      1: { cellWidth: 36 }, 2: { cellWidth: 20 },
      3: { cellWidth: 17, halign: 'center' }, 4: { cellWidth: 19, halign: 'center' },
      5: { cellWidth: 19, halign: 'center' }, 6: { cellWidth: 22, halign: 'center' }
    },
    headStyles: { fillColor: C.band, textColor: C.white, fontStyle: 'bold', fontSize: 7.5, valign: 'middle' },
    didParseCell: (data) => {
      const bucket = BUCKETS[data.column.index - 3];
      if (data.section === 'body' && bucket && data.cell.raw > 0) {
        data.cell.styles.textColor = bucket.color;
        data.cell.styles.fontStyle = 'bold';
      }
    }
  });
  ctx.y = ctx.doc.lastAutoTable.finalY + 10;

  if (sitesWithout.length > 0) {
    drawNote(
      ctx,
      `${ctx.tx('sitesWithout')} (${sitesWithout.length})`,
      sitesWithout.map((s) => s.nom_site).join('  ·  '),
      C.text,
      C.bg
    );
  }

  // Un chapitre par diagnostic
  for (const entry of sorted) {
    ctx.runningTitle = `${entry.site.nom_site} — ${scopeLabel(ctx, entry.assessment)}`;
    newPage(ctx);
    drawAssessmentBanner(ctx, entry);
    drawAssessmentBody(ctx, entry);
  }

  finalize(ctx, ctx.tx('bookLabel'));
  const today = new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Paris' });
  return { doc: ctx.doc, filename: `diagnostics-sites-${today}.pdf` };
}
